import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  clamp,
  hexToHsl,
  hexToRgb,
} from '@/components/layout/themeColorUtils';
/**
 * Theme-color system for the workspace UI.
 *
 * A single "base color" drives the entire warm workspace palette
 * (accent, surfaces, borders, text). Every shade, tint and neutral is
 * generated automatically from the base hue and written to the
 * `--inv-*` CSS custom properties on :root. Tailwind `@layer base` in
 * `tailwind.css` declares the defaults; picking a color recolors instantly.
 *
 * When no color is chosen, those CSS defaults apply.
 */

export interface BaseSwatch {
  id: string;
  label: string;
  color: string;
}

export interface ThemeCombination {
  id: string;
  label: string;
  base: string;
  /** Optional secondary accent used as the lighter accent layer. */
  secondary?: string;
  isDefault?: boolean;
}

interface ThemeColorContextValue {
  baseColor: string | null;
  combinationId: string | null;
  setBaseColor: (hex: string | null, combinationId?: string | null) => void;
  applyCombination: (combination: ThemeCombination) => void;
  reset: () => void;
  baseSwatches: BaseSwatch[];
  combinations: ThemeCombination[];
  /** The effective accent currently shown (default or chosen). */
  effectiveAccent: string;
}

/** Bump this suffix to discard saved themes when the default base color changes. */
const STORAGE_KEY = 'inv-theme-color-v3';
const DEFAULT_ACCENT = '#0046b8';

const BASE_SWATCHES: BaseSwatch[] = [
  { id: 'brand-blue', label: 'Brand Blue', color: DEFAULT_ACCENT },
  { id: 'amber', label: 'Amber', color: '#b9770f' },
  { id: 'olive', label: 'Olive', color: '#6f7d3a' },
  { id: 'emerald', label: 'Emerald', color: '#2f7d5b' },
  { id: 'teal', label: 'Teal', color: '#2a7d7d' },
  { id: 'blue', label: 'Blue', color: '#3a6ea5' },
  { id: 'indigo', label: 'Indigo', color: '#4f4b8f' },
  { id: 'plum', label: 'Plum', color: '#84436f' },
];

const COMBINATIONS: ThemeCombination[] = [
  {
    id: 'blue-stone',
    label: 'Blue & Stone',
    base: DEFAULT_ACCENT,
    secondary: '#8a847c',
    isDefault: true,
  },
  { id: 'indigo-blue', label: 'Indigo & Blue', base: '#4f4b8f', secondary: '#0046b8' },
  { id: 'teal-coral', label: 'Teal & Coral', base: '#2a7d7d', secondary: '#e2553a' },
];

/* ──────────────────────────── color math ──────────────────────────── */

const hsl = (h: number, s: number, l: number): string =>
  `hsl(${Math.round(h)}, ${Math.round(clamp(s, 0, 100))}%, ${Math.round(
    clamp(l, 0, 100),
  )}%)`;
/** Relative luminance (0..1) used to pick readable text on the accent. */
const relativeLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

type Palette = Record<string, string>;

/**
 * Builds the full `--inv-*` token set from a single base color.
 * Neutrals are tinted with the base hue at low saturation so the whole
 * surface feels cohesive ("auto neutral").
 */
const buildPalette = (base: string, secondary?: string): Palette => {
  const { h, s } = hexToHsl(base);
  const onAccent = relativeLuminance(base) > 0.6 ? '#2b2118' : '#ffffff';
  const [br, bg, bb] = hexToRgb(base);

  const accent2 = secondary || hsl(h, clamp(s + 6, 0, 100), 58);

  return {
    '--inv-accent': base,
    '--inv-accent-hover': hsl(h, s, 28),
    '--inv-accent-2': accent2,
    '--inv-accent-soft': hsl(h, clamp(s * 0.85, 40, 90), 94),
    '--inv-accent-soft-2': hsl(h, clamp(s * 0.8, 30, 85), 96.5),
    '--inv-on-accent': onAccent,

    '--inv-bg': hsl(h, 28, 97),
    '--inv-surface': hsl(h, 40, 99.5),
    '--inv-surface-muted': hsl(h, 26, 96),
    '--inv-surface-muted-2': hsl(h, 24, 94),
    '--inv-surface-card': hsl(h, 24, 94),

    '--inv-border': hsl(h, 20, 88),
    '--inv-border-strong': hsl(h, 18, 80),
    '--inv-border-soft': hsl(h, 22, 91),
    '--inv-border-soft-2': hsl(h, 20, 90),

    '--inv-text': hsl(h, 12, 16),
    '--inv-text-body': hsl(h, 10, 28),
    '--inv-text-secondary': hsl(h, 8, 40),
    '--inv-text-muted': hsl(h, 6, 52),
    '--inv-text-tertiary': hsl(h, 6, 58),
    '--inv-separator': hsl(h, 10, 80),

    '--inv-accent-ring': `rgba(${br}, ${bg}, ${bb}, 0.14)`,
    '--inv-accent-rgb': `${br}, ${bg}, ${bb}`,
    '--inv-shadow': '0 4px 20px -4px rgba(45, 42, 38, 0.05)',
    '--inv-shadow-hover': '0 12px 28px -6px rgba(45, 42, 38, 0.1)',

    '--inv-sidebar-bg': hsl(h, clamp(s * 0.45, 18, 42), 12),
    '--inv-sidebar-border': hsl(h, clamp(s * 0.35, 14, 36), 20),
    '--inv-sidebar-divider': hsl(h, clamp(s * 0.3, 12, 32), 24),
    '--inv-sidebar-nav-active': hsl(h, clamp(s * 0.4, 16, 40), 18),
    '--inv-sidebar-nav-hover': hsl(h, clamp(s * 0.35, 14, 36), 15),
    '--inv-sidebar-icon': hsl(h, clamp(s * 0.15, 8, 24), 62),
    '--inv-sidebar-fg': hsl(h, 18, 96),
  };
};

const INV_TOKEN_KEYS = Object.keys(buildPalette(DEFAULT_ACCENT));

const applyPaletteToRoot = (palette: Palette | null) => {
  const root = document.documentElement;
  if (!palette) {
    // Revert to the CSS defaults in tailwind.css @layer base.
    INV_TOKEN_KEYS.forEach(key => root.style.removeProperty(key));
    return;
  }
  Object.entries(palette).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

/* ──────────────────────────── persistence ─────────────────────────── */

interface StoredTheme {
  base: string;
  combinationId?: string | null;
  secondary?: string;
}

const readStored = (): StoredTheme | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTheme;
    if (parsed && typeof parsed.base === 'string') return parsed;
  } catch {
    /* ignore */
  }
  return null;
};

const writeStored = (value: StoredTheme | null) => {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/* ──────────────────────────── context ─────────────────────────────── */

const ThemeColorContext = createContext<ThemeColorContextValue>({
  baseColor: null,
  combinationId: null,
  setBaseColor: () => {},
  applyCombination: () => {},
  reset: () => {},
  baseSwatches: BASE_SWATCHES,
  combinations: COMBINATIONS,
  effectiveAccent: DEFAULT_ACCENT,
});

export const ThemeColorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const stored = readStored();
  const [baseColor, setBaseColorState] = useState<string | null>(
    stored?.base ?? null,
  );
  const [secondary, setSecondary] = useState<string | undefined>(
    stored?.secondary,
  );
  const [combinationId, setCombinationId] = useState<string | null>(
    stored?.combinationId ?? null,
  );

  useEffect(() => {
    if (baseColor) {
      applyPaletteToRoot(buildPalette(baseColor, secondary));
    } else {
      applyPaletteToRoot(null);
    }
  }, [baseColor, secondary]);

  const setBaseColor = useCallback(
    (hex: string | null, comboId: string | null = null) => {
      setCombinationId(comboId);
      setSecondary(undefined);
      setBaseColorState(hex);
      writeStored(hex ? { base: hex, combinationId: comboId } : null);
    },
    [],
  );

  const applyCombination = useCallback((combination: ThemeCombination) => {
    setCombinationId(combination.id);
    setSecondary(combination.secondary);
    setBaseColorState(combination.base);
    writeStored({
      base: combination.base,
      combinationId: combination.id,
      secondary: combination.secondary,
    });
  }, []);

  const reset = useCallback(() => {
    setCombinationId(null);
    setSecondary(undefined);
    setBaseColorState(null);
    writeStored(null);
  }, []);

  const value = useMemo<ThemeColorContextValue>(
    () => ({
      baseColor,
      combinationId,
      setBaseColor,
      applyCombination,
      reset,
      baseSwatches: BASE_SWATCHES,
      combinations: COMBINATIONS,
      effectiveAccent: baseColor ?? DEFAULT_ACCENT,
    }),
    [baseColor, combinationId, setBaseColor, applyCombination, reset],
  );

  return (
    <ThemeColorContext.Provider value={value}>
      {children}
    </ThemeColorContext.Provider>
  );
};

export const useThemeColor = () => useContext(ThemeColorContext);
